import { useMemo, useRef } from 'react';
import ActionButtons from '../../components/common/ActionButtons';
import SimpleModal from '../../components/common/simple-modal/SimpleModal';
import { IAddressValues, OperationType, ParameterGroup } from '../../types/SetpointManagerTypes';
import WriteConfirmGroup from './WriteConfirmGroup';
import './write-confirm.scss';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { resetReadWriteItems, setOperation } from './parameterGroupsSlice';
import { useToastService } from '../../components/toast/ToastService';

interface WriteConfirmProps {
  open: boolean;
  handleCancel: () => void;
  groups: ParameterGroup[];
  onConfirm: (addressValues: IAddressValues) => Promise<unknown>;
  operation?: OperationType;
}
const WriteConfirm: React.FC<WriteConfirmProps> = ({ open, handleCancel, groups, onConfirm, operation = 'write' }) => {
  const writeItems = useAppSelector((state) => state.parameterGroups.writeItems);
  const readItems = useAppSelector((state) => state.parameterGroups.readItems);
  const selectedItems = useAppSelector((state) => state.parameterGroups.selectedRows);
  const { showErrorToast, showInfoToast, showSuccessToast } = useToastService();
  const confirmClickRef = useRef(false);
  const dispatch = useAppDispatch();
  const data = useMemo(() => {
    if (!open) return [];
    if (operation === 'restore') {
      const selected = new Set(selectedItems);
      const restoreGroups = groups
        .map((group) => ({
          groupName: group.setpointGroupName,
          setpoints: group.setpoints.filter((sp) => selected.has(sp.parameter)),
        }))
        .filter((item) => item.setpoints.length);
      return restoreGroups;
    }
    if (operation === 'backup') {
      const selected = new Set(Object.keys(readItems));
      const backupGroups = groups
        .map((group) => ({
          groupName: group.setpointGroupName,
          setpoints: group.setpoints.filter((sp) => selected.has(sp.parameter)),
        }))
        .filter((item) => item.setpoints.length);
      return backupGroups;
    }

    // default: write operation uses writeItems
    const writeGroups = groups
      .map((group) => ({
        groupName: group.setpointGroupName,
        setpoints: group.setpoints.filter((sp) => Object.prototype.hasOwnProperty.call(writeItems, sp.parameter)),
      }))
      .filter((item) => item.setpoints.length);
    return writeGroups;
  }, [open, operation, groups, writeItems, readItems]);
  const handleConfirm = () => {
    if (confirmClickRef.current) return;
    confirmClickRef.current = true;
    dispatch(setOperation(operation));
    const payload =
      operation === 'restore'
        ? (() => {
            const addressToCurrentValue: IAddressValues = {};
            data.forEach((g) =>
              g.setpoints.forEach((sp) => {
                addressToCurrentValue[sp.parameter] = String(sp.backupValue ?? '');
              }),
            );
            return addressToCurrentValue;
          })()
        : operation === 'backup'
          ? (() => {
              // Build addressValues from readItems (true) using currentValue from groups
              const addressToCurrentValue: IAddressValues = {};
              groups.forEach((g) =>
                g.setpoints.forEach((sp) => {
                  addressToCurrentValue[sp.parameter] = String(sp.currentValue ?? '');
                }),
              );
              const entries = Object.entries(readItems);
              const out: IAddressValues = {};
              for (const [address, isSelected] of entries) {
                if (isSelected) {
                  const val = addressToCurrentValue[address];
                  if (val !== undefined) {
                    out[address] = val;
                  }
                }
              }
              return out;
            })()
          : writeItems;

    // Show info toast before starting backup operation
    if (operation === 'backup') {
      showInfoToast({ headerMessage: 'Creating setpoints backup' });
    } else if (operation === 'restore') {
      showInfoToast({ headerMessage: 'Restoring backups' });
    }

    onConfirm(payload)
      .then(() => {
        dispatch(resetReadWriteItems());
        handleCancel();
        if (operation === 'restore') {
          showSuccessToast('Restored backups successfully');
        } else if (operation === 'backup') {
          showSuccessToast('Setpoints backup created successfully');
        } else {
          showInfoToast({ headerMessage: 'Writing setpoints' });
        }
      })
      .catch(() => {
        showErrorToast(
          operation === 'restore'
            ? 'Restore backups failed'
            : operation === 'backup'
              ? 'Setpoints backup failed'
              : 'Setpoints write failed',
        );
      })
      .finally(() => {
        confirmClickRef.current = false;
      });
  };

  if (!open) {
    return <></>;
  }
  return (
    <SimpleModal
      onClose={handleCancel}
      header={
        operation === 'restore'
          ? 'Confirm restore setpoints'
          : operation === 'backup'
            ? 'Confirm backup setpoints'
            : 'Confirm write setpoints'
      }
      className='write-confirm-modal'
    >
      <div className='p-[24px]'>
        <div className='text-[#b8c5cc] mb-[16px]'>
          {operation === 'restore'
            ? 'List of setpoints to restore:'
            : operation === 'backup'
              ? 'List of setpoints to back up:'
              : 'List of setpoints to write:'}
        </div>
        <div className='overflow-auto' style={{ maxHeight: '300px' }}>
          {data.length ? (
            data.map((item) => {
              return (
                <WriteConfirmGroup
                  data={item.setpoints}
                  key={item.groupName}
                  name={item.groupName}
                  operation={operation}
                />
              );
            })
          ) : (
            <div className='text-white'>No data available</div>
          )}
        </div>
      </div>
      <ActionButtons buttonText='Proceed' onSubmit={handleConfirm} onCancel={handleCancel} />
    </SimpleModal>
  );
};
export default WriteConfirm;
